import { bookmarkService } from './bookmarkService';
import { offlineStorageService } from './offlineStorageService';
import { networkService } from './networkService';

class SyncService {
  // Auto-sync when network comes back online
  constructor() {
    this.isSyncing = false;
    this.syncListeners = new Set();
    this.currentUserId = null;
    this.lastSyncTime = 0;
    this.syncDebounceTime = 5000; // 5 seconds minimum between syncs
    
    // Auto-sync when network comes back online
    networkService.addNetworkListener((isOnline) => {
      if (isOnline && !this.isSyncing && this.currentUserId) {
        // Check if enough time has passed since last sync
        const now = Date.now();
        if (now - this.lastSyncTime < this.syncDebounceTime) {
          // console.log('🔄 Sync debounced - too soon since last sync');
          return;
        }
        
        // Wait a bit for connection to stabilize
        // console.log('🌐 Network reconnected, scheduling sync...');
        setTimeout(() => this.syncUserData(this.currentUserId), 2000);
      }
    });
  }

  // Set current user for auto-sync
  setCurrentUser(userId) {
    this.currentUserId = userId;
    // If user is set and online, consider an initial sync.
    // The actual scheduling is handled by scheduleInitialSync if called from elsewhere.
    if (userId && networkService.getNetworkStatus()) {
      // console.log(`SYNC_SERVICE: User set to ${userId}. Network is online.`);
    } else if (!userId) {
      // console.log("SYNC_SERVICE: User cleared (logged out).");
    }
  }

  // Method to ensure local cache is primed, typically after login when online.
  async scheduleInitialSync(userId) {
    if (!userId || !networkService.getNetworkStatus()) {
      // console.log(`SYNC_SERVICE: Initial sync for ${userId} skipped (offline or no user).`);
      return;
    }

    if (this.isSyncing) {
      // console.log(`SYNC_SERVICE: Initial sync for ${userId} skipped (already syncing).`);
      return;
    }

    const now = Date.now();
    // Allow sync if lastSyncTime is 0 (first ever sync) or debounce time has passed
    if (this.lastSyncTime !== 0 && (now - this.lastSyncTime < this.syncDebounceTime)) {
      // console.log(`SYNC_SERVICE: Initial sync for ${userId} debounced (too soon since last sync). Last sync: ${this.lastSyncTime}, Now: ${now}`);
      return;
    }

    // console.log(`SYNC_SERVICE: Scheduling initial sync for ${userId} to prime local cache.`);
    // Using a short delay to allow other init processes to settle.
    setTimeout(() => {
      // Re-check conditions inside setTimeout, as state might have changed.
      // Ensure the user for whom sync was scheduled is still the current one.
      if (this.currentUserId === userId && networkService.getNetworkStatus() && !this.isSyncing) {
        // console.log(`SYNC_SERVICE: Executing scheduled initial sync for ${userId}.`);
        this.syncUserData(userId); // Not awaiting here, let it run in background
      } else {
        // console.log(`SYNC_SERVICE: Skipped execution of scheduled initial sync for ${userId} (conditions changed: current user ${this.currentUserId}, online ${networkService.getNetworkStatus()}, syncing ${this.isSyncing}).`);
      }
    }, 1000); // 1 second delay
  }

  // Add listener for sync status changes
  addSyncListener(callback) {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  // Notify all listeners of sync status
  notifySyncListeners(status) {
    this.syncListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in sync status listener:', error);
      }
    });
  }

  // Simple sync: just sync offline items to server, then refresh from server
  async syncUserData(userId = null) {
    if (this.isSyncing || !networkService.getNetworkStatus()) {
      return;
    }
    this.isSyncing = true;
    // console.log(`SYNC_SERVICE: Starting sync for user: ${userId}`);
    this.notifySyncListeners({ type: 'start', message: 'Syncing data...' });

    try {
      if (!userId) throw new Error('User ID required for sync');

      await offlineStorageService.cleanupOrphanedOfflineBookmarks(userId);

      const offlineBookmarksInitial = await offlineStorageService.getBookmarks(userId);
      const offlineCreated = offlineBookmarksInitial.filter(b => b.isOffline);
      
      // Only log if there are items to sync
      if (offlineCreated.length > 0) {
        // console.log(`SYNC_SERVICE: Found ${offlineCreated.length} bookmarks with isOffline=true. IDs: ${offlineCreated.map(b => `${b.id} (isOffline: ${b.isOffline})`).join(', ')}`);
      }

      const successfullySyncedServerBookmarks = []; // Store server version of successfully synced items

      for (const bookmark of offlineCreated) {
        try {
          const { isOffline, isOfflineUpdated, ...cleanBookmark } = bookmark;
          // console.log(`SYNC_SERVICE: Attempting to upload offline bookmark (Offline ID: ${bookmark.id}, Title: ${cleanBookmark.title})`);
          const serverBookmark = await bookmarkService.addBookmark(cleanBookmark, userId);
          // console.log(`SYNC_SERVICE: Successfully synced offline bookmark (Offline ID: ${bookmark.id}) to server (New Server ID: ${serverBookmark.id}).`);
          successfullySyncedServerBookmarks.push({ offlineId: bookmark.id, serverBookmark });
        } catch (error) {
          console.error(`SYNC_SERVICE: Failed to sync bookmark (Offline ID: ${bookmark.id}, Title: ${bookmark.title}):`, error);
        }
      }

      if (successfullySyncedServerBookmarks.length > 0) {
        // console.log(`SYNC_SERVICE: Deleting ${successfullySyncedServerBookmarks.length} successfully synced offline items from IndexedDB.`);
        for (const { offlineId } of successfullySyncedServerBookmarks) {
          // console.log(`SYNC_SERVICE: Attempting to delete original offline item with ID: ${offlineId}`);
          await offlineStorageService.deleteBookmark(offlineId, userId);
        }
      }
      
      // Log state of IndexedDB *after* deletions and *before* getting fresh server data for final save
      const bookmarksAfterDeletions = await offlineStorageService.getBookmarks(userId);
      // console.log(`SYNC_SERVICE: State of IndexedDB after deleting synced items. Found ${bookmarksAfterDeletions.length} items. Items: ${bookmarksAfterDeletions.map(b => `${b.id} (isOffline: ${b.isOffline})`).join('; ')}`);

      // console.log(`SYNC_SERVICE: Refreshing all data from server...`);
      const allServerBookmarks = await bookmarkService.getBookmarks(userId);
      // console.log(`SYNC_SERVICE: Fetched ${allServerBookmarks.length} bookmarks from server.`);
      
      // Check for any local bookmarks that are still marked as offline (e.g., failed to sync earlier)
      // This read should be after deletions and before the final save operation.
      const remainingLocalBookmarks = await offlineStorageService.getBookmarks(userId); // Re-fetch to be sure
      const stillMarkedOffline = remainingLocalBookmarks.filter(b => b.isOffline);

      if (stillMarkedOffline.length > 0) {
         console.warn(`SYNC_SERVICE: Found ${stillMarkedOffline.length} items STILL marked as isOffline in IndexedDB before final save. These might be items that failed to sync. IDs: ${stillMarkedOffline.map(b => b.id).join(', ')}`);
      }

      let finalBookmarksToSave = [...allServerBookmarks];
      // Use a Set to keep track of IDs already added to finalBookmarksToSave to ensure uniqueness
      const finalIds = new Set(allServerBookmarks.map(b => b.id));

      // Add any genuinely unsynced (stillMarkedOffline) items.
      // This also de-duplicates items from stillMarkedOffline if offlineStorageService returned multiple copies.
      for (const offlineItem of stillMarkedOffline) {
        if (!finalIds.has(offlineItem.id)) { // Check if this ID is already in finalBookmarksToSave
          console.warn(`SYNC_SERVICE: Adding item from stillMarkedOffline (ID: ${offlineItem.id}, Title: ${offlineItem.title}) to final save list.`);
          finalBookmarksToSave.push(offlineItem);
          finalIds.add(offlineItem.id); // Record that this ID has been added
        } else {
           // This log covers two cases:
           // 1. The offlineItem.id was already present in allServerBookmarks (very unlikely if server IDs are distinct from offline ID format).
           // 2. The offlineItem.id was already added from a *previous item* in the stillMarkedOffline list (i.e., stillMarkedOffline had duplicates). This is the primary duplication we are preventing here.
           console.warn(`SYNC_SERVICE: Item from stillMarkedOffline (ID: ${offlineItem.id}, Title: ${offlineItem.title}) is already in finalBookmarksToSave (either from server or an earlier processed offline item). Skipping to prevent duplication.`);
        }
      }
      
      // console.log(`SYNC_SERVICE: Preparing to save ${finalBookmarksToSave.length} bookmarks to IndexedDB (after combining server data and any remaining valid offline items).`);
      // console.log(`SYNC_SERVICE: Final list to save to IndexedDB: ${finalBookmarksToSave.map(b => `${b.id} (isOffline: ${b.isOffline === undefined ? 'undefined->false' : b.isOffline})`).join('; ')}`);
      await offlineStorageService.saveBookmarks(finalBookmarksToSave, userId); // This will set isOffline: false for all of them

      // console.log(`SYNC_SERVICE: Sync completed. ${finalBookmarksToSave.length} bookmarks in local store.`);
      this.notifySyncListeners({ type: 'success', message: 'Data synced successfully', bookmarksCount: finalBookmarksToSave.length });
    } catch (error) {
      console.error(`SYNC_SERVICE: Sync failed:`, error);
      this.notifySyncListeners({ type: 'error', message: `Sync failed: ${error.message}` });
    } finally {
      this.lastSyncTime = Date.now();
      this.isSyncing = false;
      this.notifySyncListeners({ type: 'end' });
      // console.log('SYNC_SERVICE: Sync process finished.');
    }
  }

  // Force sync (useful for manual refresh)
  async forcSync(userId) {
    if (this.isSyncing) {
      // console.log('🔄 Force sync skipped - already syncing');
      return;
    }
    
    // Check connectivity first
    const isConnected = await networkService.testConnectivity();
    if (!isConnected) {
      throw new Error('No internet connection available');
    }
    
    await this.syncUserData(userId);
  }

  // Get sync status
  getSyncStatus() {
    return {
      isSyncing: this.isSyncing,
      isOnline: networkService.getNetworkStatus()
    };
  }

  // Check if there are offline changes to sync
  async hasPendingChanges(userId) {
    const offlineBookmarks = await offlineStorageService.getBookmarks(userId);
    const offlineCreated = offlineBookmarks.filter(b => b.isOffline);
    
    return offlineCreated.length > 0;
  }
}

export const syncService = new SyncService();
